import { Link } from 'react-router';
import type { EventDto } from '~/lib/types';
import { AREA_LABELS } from '~/lib/labels';
import { groupByDay } from '~/lib/event-utils';

const FULL_WEEKDAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/** Full weekday from an ET dayKey ('YYYY-MM-DD'), parsed as UTC to stay stable. */
function weekdayFromKey(dayKey: string): string {
  const dow = new Date(`${dayKey}T00:00:00Z`).getUTCDay();
  return FULL_WEEKDAYS[dow] ?? '';
}

/** 'Wed, Jul 8' -> 'Jul 8' */
function dateOnly(dateLabel: string): string {
  const comma = dateLabel.indexOf(',');
  return comma > 0 ? dateLabel.slice(comma + 2) : dateLabel;
}

export function EventRow({ event }: { event: EventDto }) {
  return (
    <article className="event-row">
      <time className="event-row-time" dateTime={event.iso}>
        {event.timeLabel}
      </time>
      <div className="event-row-main">
        <h3 className="event-row-title">
          <Link to={`/events/${event.slug}`}>{event.title}</Link>
        </h3>
        <p className="event-row-meta">
          {event.organizerName}
          {event.locationName ? ` · ${event.locationName}` : ''}
        </p>
      </div>
      <div className="event-row-tags">
        <span className="tag">{AREA_LABELS[event.area]}</span>
        {event.isFree ? (
          <span className="tag tag-free">Free</span>
        ) : (
          <span className="tag tag-cost">{event.cost || 'Paid'}</span>
        )}
      </div>
    </article>
  );
}

function DayHeading({
  dayKey,
  dateLabel,
  isToday,
}: {
  dayKey: string;
  dateLabel: string;
  isToday: boolean;
}) {
  return (
    <h2 className={isToday ? 'day-heading is-today' : 'day-heading'}>
      <span className="dow">{weekdayFromKey(dayKey)}</span>
      <span className="date">{dateOnly(dateLabel)}</span>
      {isToday && <span className="today-flag">Today</span>}
    </h2>
  );
}

export function EmptyState({
  title = 'Nothing listed for these filters yet.',
  children,
}: {
  title?: string;
  children?: React.ReactNode;
}) {
  return (
    <div className="empty-state">
      <h3 className="empty-title">{title}</h3>
      {children ?? (
        <>
          <p>This space is free — a listing takes about two minutes.</p>
          <Link to="/submit" className="ui-btn btn-primary">
            List your event — free
          </Link>
        </>
      )}
    </div>
  );
}

/**
 * Agenda view — events grouped by day. Each day is a heavy condensed uppercase
 * heading with the date beneath; events are an editorial list beside it (rows
 * of time · title · organizer · area · Free), typography doing the work.
 */
export function EventAgenda({ events, todayKey }: { events: EventDto[]; todayKey: string }) {
  const groups = groupByDay(events);

  if (events.length === 0) return <EmptyState />;

  return (
    <div className="agenda">
      {groups.map((g) => (
        <section key={g.dayKey} className="day-group" aria-label={g.dateLabel}>
          <div className="day-rail">
            <DayHeading dayKey={g.dayKey} dateLabel={g.dateLabel} isToday={g.dayKey === todayKey} />
          </div>
          <div className="day-events">
            {g.events.map((e) => (
              <EventRow key={e.id} event={e} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
